/** Today: what the three engine screens found, on one page - a greeting
 *  and an ask box, a strip of figures, "Do today" ranked by money, and a
 *  column with the engines' own figures, the open anomalies and who owes
 *  most, each pointing at the screen that holds the rows
 *  (backend/chainlit/today_engine.py). Nothing is counted here.
 *
 *  Rich layout since 2026-11-05 (Shady: the narrow column read as a report,
 *  not a product). Socketless (App.tsx SOCKETLESS_ROUTES); the first page
 *  after sign-in for a company that has any engine screen (pages/Home.tsx
 *  sends them here once per browser session). The server refuses admins.
 */
import { ArrowRight, Loader2, Search } from 'lucide-react';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiAnomalySummary } from '@/components/anomalies/api';
import KpiStrip from '@/components/engine/Kpis';
import { TodayPage, fetchToday } from '@/components/engine/api';
import { Header } from '@/components/header';
import { isPlainClick } from '@/components/header/sections';
import { surface, useAsk } from '@/components/know-your-data/blocks';
import { SidebarProvider } from '@/components/ui/sidebar';

const POLL_MS = 4000;
const MAX_POLLS = 30;

const ENGINE_TONE: Record<string, React.CSSProperties> = {
  cash: { background: 'var(--sig-chip)', color: 'var(--sig-blue-dark)' },
  sales: { background: 'var(--sig-warn-bg)', color: 'var(--sig-warn-text)' },
  margin: {
    background: 'rgba(5, 150, 105, 0.10)',
    color: 'var(--sig-positive)'
  }
};
const ENGINE_SHORT: Record<string, string> = {
  cash: 'Cash',
  sales: 'Sales',
  margin: 'Margin'
};

export default function Today() {
  const [page, setPage] = useState<TodayPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [anomalies, setAnomalies] = useState<number | null>(null);
  const polls = useRef(0);

  const load = useCallback(() => {
    setError(null);
    fetchToday()
      .then((p) => {
        setPage(p);
        if (p.building && polls.current < MAX_POLLS) {
          polls.current += 1;
          window.setTimeout(load, POLL_MS);
        }
      })
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'Could not load this page.')
      );
  }, []);

  useEffect(() => {
    load();
    let cancelled = false;
    apiAnomalySummary()
      .then((s) => {
        if (!cancelled) setAnomalies(s.owner_open_count ?? s.open_count ?? 0);
      })
      .catch(() => {
        if (!cancelled) setAnomalies(null);
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full flex-col">
        <Header />
        <div className="flex-1 overflow-auto" id="today-scroll">
          <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-6 sm:py-6">
            <TopBar page={page} />
            {error ? (
              <div className="rounded-xl border p-4 text-sm" style={surface}>
                <p className="text-[var(--sig-ink)]">{error}</p>
                <button
                  type="button"
                  className="mt-2 text-sm font-medium text-primary hover:underline"
                  onClick={load}
                >
                  Try again
                </button>
              </div>
            ) : !page ? (
              <div
                className="grid place-items-center rounded-xl border px-6 py-16"
                style={surface}
              >
                <Loader2 className="size-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <Body page={page} anomalies={anomalies} />
            )}
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}

/** The greeting, the day and the ask box: a question typed here starts a
 *  new conversation with it, as "Ask Signals" does on every finding. */
function TopBar({ page }: { page: TodayPage | null }) {
  const { ask } = useAsk();
  const [q, setQ] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const text = q.trim();
    if (text) ask(text);
  };
  return (
    <div className="mb-5 flex flex-wrap items-center gap-3">
      <div className="min-w-0">
        <h1 className="text-[22px] font-bold leading-snug tracking-tight text-[var(--sig-ink)]">
          {page?.greeting ?? 'Today'}
        </h1>
        <p className="text-[13px] text-muted-foreground">
          {[page?.day, page?.subtitle].filter(Boolean).join(' · ')}
        </p>
      </div>
      <form
        onSubmit={submit}
        className="flex h-10 w-full max-w-[420px] items-center gap-2 rounded-xl border px-3.5 sm:ms-auto"
        style={{ ...surface, borderColor: 'var(--sig-border)' }}
      >
        <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <label htmlFor="today-ask" className="sr-only">
          Ask Signals
        </label>
        <input
          id="today-ask"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Ask about a customer, an item, or anything in your data"
          className="min-w-0 flex-1 bg-transparent text-[13.5px] text-[var(--sig-ink)] outline-none placeholder:text-muted-foreground"
        />
      </form>
    </div>
  );
}

function Body({
  page,
  anomalies
}: {
  page: TodayPage;
  anomalies: number | null;
}) {
  const navigate = useNavigate();
  const go = (e: React.MouseEvent, path: string) => {
    if (!isPlainClick(e)) return;
    e.preventDefault();
    navigate(path);
  };
  const kpis = page.kpis ?? [];
  return (
    <div className="space-y-5">
      {kpis.length ? (
        <KpiStrip kpis={kpis} lead />
      ) : (
        <Tiles page={page} go={go} />
      )}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.55fr)_minmax(320px,1fr)] lg:items-start">
        <section className="min-w-0 rounded-xl border" style={surface}>
          <div className="flex items-center gap-2.5 px-4 pb-2 pt-3.5">
            <h2 className="text-[15px] font-bold text-[var(--sig-ink)]">
              Do today
            </h2>
            <span className="text-xs text-muted-foreground">
              ranked by the money behind each item
            </span>
          </div>
          {page.items.length ? (
            <ol className="list-none">
              {page.items.map((i, n) => (
                <li
                  key={`${i.engine}-${i.section}-${n}`}
                  className="border-t"
                  style={{ borderColor: 'var(--sig-border-row)' }}
                >
                  <a
                    href={i.page}
                    onClick={(e) => go(e, i.page)}
                    className="flex items-start gap-3 px-4 py-3 text-[var(--sig-ink)] hover:bg-[var(--sig-chip)]"
                  >
                    <span
                      className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold"
                      style={ENGINE_TONE[i.engine] ?? ENGINE_TONE.cash}
                    >
                      {n + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium">
                        {i.title}
                      </span>
                      <span className="block text-[12.5px] text-muted-foreground">
                        {i.text}{' '}
                        <span
                          className="ms-1 inline-flex h-[20px] items-center rounded-full px-2 text-[11px] font-semibold"
                          style={ENGINE_TONE[i.engine] ?? ENGINE_TONE.cash}
                        >
                          {ENGINE_SHORT[i.engine] ?? i.engine} Signals
                        </span>
                      </span>
                    </span>
                    <span
                      className="shrink-0 text-sm font-bold tabular-nums"
                      dir="ltr"
                    >
                      {i.display}
                    </span>
                  </a>
                </li>
              ))}
            </ol>
          ) : page.building ? (
            <p className="border-t px-4 py-4 text-sm text-muted-foreground">
              Signals is counting your data. The first view after each nightly
              update takes up to a minute.
            </p>
          ) : (
            <p className="border-t px-4 py-4 text-sm text-muted-foreground">
              {page.empty}
            </p>
          )}
        </section>

        <div className="flex min-w-0 flex-col gap-4">
          <section
            className="flex flex-col gap-2 rounded-xl border p-4"
            style={surface}
          >
            <h2 className="text-[15px] font-bold text-[var(--sig-ink)]">
              Your engines
            </h2>
            {page.tiles.map((t) => (
              <a
                key={t.engine}
                href={t.page}
                onClick={(e) => go(e, t.page)}
                className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-[var(--sig-ink)] hover:bg-[var(--sig-chip)]"
                style={{ background: 'var(--sig-table-head)' }}
              >
                <span
                  className="inline-flex h-[22px] shrink-0 items-center rounded-full px-2.5 text-[11.5px] font-semibold"
                  style={ENGINE_TONE[t.engine] ?? ENGINE_TONE.cash}
                >
                  {ENGINE_SHORT[t.engine] ?? t.title}
                </span>
                {t.building ? (
                  <span className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
                    <Loader2 className="size-3.5 animate-spin" /> Counting
                  </span>
                ) : (
                  <span className="min-w-0 text-[13.5px]">
                    <b dir="ltr">{t.value ?? '–'}</b>{' '}
                    <span className="text-muted-foreground">{t.label}</span>
                  </span>
                )}
                {t.change ? (
                  <span className="ms-auto shrink-0 text-[11.5px] text-muted-foreground">
                    {t.change}
                  </span>
                ) : null}
              </a>
            ))}
          </section>

          {anomalies && anomalies > 0 ? (
            <a
              href="/anomalies"
              onClick={(e) => go(e, '/anomalies')}
              className="flex items-center gap-3 rounded-xl border p-4 text-[var(--sig-ink)] hover:border-[var(--sig-blue)]"
              style={surface}
            >
              <span className="text-2xl font-bold tabular-nums">
                {anomalies}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-bold">
                  {anomalies === 1 ? 'anomaly needs you' : 'anomalies need you'}
                </span>
                <span className="block text-[12.5px] text-muted-foreground">
                  open findings from last night's checks, the biggest first
                </span>
              </span>
              <ArrowRight className="size-4 shrink-0 text-muted-foreground" />
            </a>
          ) : null}

          {page.top?.length ? (
            <section className="rounded-xl border" style={surface}>
              <div className="flex items-center gap-2 px-4 pb-1.5 pt-3.5">
                <h2 className="text-[15px] font-bold text-[var(--sig-ink)]">
                  Who owes you most
                </h2>
                <a
                  href="/cash"
                  onClick={(e) => go(e, '/cash')}
                  className="ms-auto text-[13px] font-semibold text-primary"
                >
                  All →
                </a>
              </div>
              {page.top.map((t) => (
                <div
                  key={t.name}
                  className="flex items-center gap-2.5 border-t px-4 py-2.5"
                  style={{ borderColor: 'var(--sig-border-row)' }}
                >
                  <span
                    className="grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold"
                    style={
                      t.signal?.kind === 'stopped'
                        ? {
                            background: 'rgba(220, 38, 38, 0.10)',
                            color: 'var(--sig-negative)'
                          }
                        : t.signal?.kind === 'growing'
                          ? ENGINE_TONE.sales
                          : ENGINE_TONE.cash
                    }
                  >
                    {(t.name.trim().match(/\p{L}/u) ?? ['?'])[0].toUpperCase()}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[13.5px] text-[var(--sig-ink)]">
                    <bdi>{t.name}</bdi>
                    {t.signal ? (
                      <span
                        className="ms-2 inline-flex h-[20px] items-center rounded-full px-2 text-[11px] font-semibold"
                        style={
                          t.signal.kind === 'stopped'
                            ? {
                                background: 'rgba(220, 38, 38, 0.10)',
                                color: 'var(--sig-negative)'
                              }
                            : ENGINE_TONE.sales
                        }
                      >
                        {t.signal.label}
                      </span>
                    ) : null}
                  </span>
                  <span
                    className="shrink-0 text-[13.5px] font-bold tabular-nums text-[var(--sig-ink)]"
                    dir="ltr"
                  >
                    {t.display}
                  </span>
                </div>
              ))}
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** The three engine tiles, for a page whose figures strip is empty (every
 *  engine still counting): the same tiles as before the rich layout. */
function Tiles({
  page,
  go
}: {
  page: TodayPage;
  go: (e: React.MouseEvent, path: string) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {page.tiles.map((t) => (
        <a
          key={t.engine}
          href={t.page}
          onClick={(e) => go(e, t.page)}
          className="flex min-h-[6.5rem] flex-col gap-1 rounded-xl border p-4 text-[var(--sig-ink)] hover:border-[var(--sig-blue)]"
          style={{ ...surface, boxShadow: 'var(--sig-shadow-card)' }}
        >
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {t.title}
          </span>
          {t.building ? (
            <span className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Counting
            </span>
          ) : (
            <>
              <span className="text-2xl font-bold tabular-nums" dir="ltr">
                {t.value ?? '–'}
              </span>
              <span className="text-xs text-muted-foreground">{t.label}</span>
            </>
          )}
        </a>
      ))}
    </div>
  );
}
